import type { NoodlNode } from '@shared/node-v1.0.0'
import { sendOutput, sendSignal } from '@shared/port-send-v1.0.0'
import ms from 'ms'
import clearAuthState, { triggerAuthClear } from '../../auth/clearAuthState'
import type { AuthStore, Props } from '../../definition'
import fetchUser from './fetchUser'
import signInSurreal from './signInSurreal'
import validateToken from './validateToken'

export default async (p: Props, noodlNode: NoodlNode) => {
	// Создадим хранилище для сравнения параметров.
	p.store = { signedIn: undefined, isLeader: false, refreshInterval: undefined }

	// Возьмем состояние с диска. addState возвращает текущее состояние, если есть, иначе создает.
	// Делаем это вначале, чтобы далее использовать новое пустое или текущее состояние на диске.
	const persistentState = await R.db.addState('auth')

	// Восстановим пользователя с диска и переподключимся с токеном если есть
	const authState = R.db?.states.auth.get()
	if (authState?.user) R.user = authState.user
	if (authState?.token && R.surreal?.db) {
		try {
			await R.surreal.db.authenticate(authState.token)
			log.info('SurrealDB authenticated with saved token on startup')
		} catch (e: any) {
			log.error('Failed to authenticate with saved token on startup', e.message)
			// Инициируем централизованный сброс состояния аутентификации
			await triggerAuthClear()
		}
	}

	// Подпишемся на изменения signedIn и inited на диске, чтобы реагировать на всех вкладках.
	// Первая подписка нужна для первичного прохода, чтобы вкладки ждали лидера.
	// Вторая подписка нужна для дальнейшего реагирования на смену состояния.
	persistentState.inited$.subscribe(async (inited: boolean) => {
		// Если инициализировано и состояние изменилось, учитывая что signedIn в store изначально undefined.
		if (inited && persistentState.signedIn !== p.store.signedIn) handleAuth(p.store, noodlNode, p)
	})
	persistentState.signedIn$.subscribe((storedSignedIn: boolean) => {
		// Сравним состояние на диске с состоянием текущей вкладки, есди инициализировано.
		if (persistentState.inited && storedSignedIn !== p.store.signedIn) handleAuth(p.store, noodlNode, p)
	})

	// Подпишемся на изменение токена, чтобы follower вкладки аутентифицировались с пользовательским токеном
	persistentState.token$.subscribe(async (token: string) => {
		// Аутентифицируемся с токеном у всех вкладок, кроме лидера
		if (!p.store.isLeader && token && R.surreal?.db) {
			try {
				// Применяем токен к существующему анонимному подключению
				await R.surreal.db.authenticate(token)
				log.info('SurrealDB authenticated with user token for follower tab')
			} catch (e: any) {
				log.error('Failed to authenticate SurrealDB with user token', e.message)
			}
		}
	})

	// Тригер на вход пользователя. Перехватывает вход у вкладки не лидера.
	persistentState.signIn$.subscribe(async (storedSignedIn: boolean) => {
		if (storedSignedIn && p.store.isLeader) {
			// Отправим состояние процесса.
			sendOutput(noodlNode, 'signingIn', true)

			// Авторизуем пользователя
			const signedIn = await signInSurreal(p, persistentState.username, persistentState.password)

			if (signedIn) {
				// Скачаем системные данные пользователя.
				await fetchUser()
				// Установим их глобально.
				const authState = R.db?.states.auth.get()
				if (authState?.user) R.user = authState.user
				// Запустим периодическую валидацию токена
				const refreshInterval = ms(p.surrealAuthRefreshInterval || '10m')
				p.store.refreshInterval = setInterval(async () => {
					const isValid = await validateToken(p)
					if (!isValid) {
						// Если токен невалиден - инициируем централизованный сброс
						await triggerAuthClear()
					}
				}, refreshInterval)
			}
			// Изменим состояние на диске для всех вкладок.
			await persistentState.set('signedIn', () => signedIn)

			// Вернем флаг на место.
			await persistentState.set('signIn', () => false)

			// Отправим состояние процесса.
			sendOutput(noodlNode, 'signingIn', false)
		}
	})

	// Подпишемся на смену лидерства. Работает одинаково при первом проходе лидера и при смене лидера.
	// Делаем это в конце, чтобы иметь готовый persistentState.
	R.db.waitForLeadership().then(async () => {
		p.store.isLeader = true

		// Установим состояние инициализации на диск, чтобы другие вкладки ждали лидера.
		await persistentState.set('inited', () => false)

		// Отменим, если оффлайн (только если используется проверка сети).
		if (p.useNetwork && !R.db.states.network?.connected) {
			handleAuth(p.store, noodlNode, p)
			return
		}

		// Проверим токен, если авторизованы - точно как в Kuzzle
		const tokenValid = persistentState.signedIn ? await validateToken(p) : false

		// Запустим периодическую валидацию токена, если токен валиден
		if (tokenValid) {
			const refreshInterval = ms(p.surrealAuthRefreshInterval || '10m')
			p.store.refreshInterval = setInterval(async () => {
				const isValid = await validateToken(p)
				if (!isValid) {
					// Инициируем централизованный сброс при невалидном токене
					await triggerAuthClear()
				}
			}, refreshInterval)
		} else {
			// Сбросим токен и пользователя при неверном токене.
			await persistentState.set('token', () => undefined)
			await persistentState.set('user', () => undefined)
		}

		// Изменим состояние на диске для всех вкладок.
		await persistentState.set('signedIn', () => tokenValid)

		// Установим состояние инициализации на диск, чтобы другие вкладки могли проверить готовность.
		await persistentState.set('inited', () => true)
	})
}

const handleAuth = async (s: AuthStore, noodlNode: NoodlNode, p: Props) => {
	const signedIn = R.db.states.auth.signedIn

	if (signedIn) {
		s.signedIn = true
		// Получаем роль по настраиваемому пути
		const rolePath = p.surrealAuthUserRolePath || 'role'
		const userRole =
			p.authBackend === 'surrealdb' ? (R.user ? R.libs.just.get(R.user, rolePath) : null) : R.user?.user?.role?.value
		sendOutput(noodlNode, 'userRole', userRole || null)
		sendSignal(noodlNode, 'signedIn')
	} else {
		s.refreshInterval && clearInterval(s.refreshInterval)
		s.signedIn = false
		// Централизованный сброс auth состояния
		await clearAuthState()
		R.surreal.db.invalidate()
		sendOutput(noodlNode, 'userRole', null)
		sendSignal(noodlNode, 'signedOut')
	}
}
